/**
 * Profile options for BakerUser
 */
export const bakerUserProfileOptions = [
  'management',
  'sales',
  'fsg',
  'fsgMuniCredit',
  'bba',
  'irrm',
  'clearing',
  'loans',
  'desk'
] as const;

export type BakerUserProfileOption = (typeof bakerUserProfileOptions)[number];

/**
 * Profiles selectable in the baker-user admin UI. Admin is granted exclusively
 * via the Entra BakerAdmin app role, not through any profile here.
 */
export const selectableBakerUserProfileOptions = bakerUserProfileOptions;

/**
 * Feature permissions selectable in the admin UI. 'admin' is intentionally absent —
 * it is managed exclusively through the Entra BakerAdmin role.
 */
export const bakerUserFeaturePermissionsOptions = [
  'inventory',
  'loans',
  'lseg',
  'dailyPricing',
  'reports',
  'portfolios',
  'cashFlows',
  'swapIQ',
  'houstonTrust',
  'empyreanImport',
  'yieldBookMonitor'
] as const;

/**
 * Feature permissions interface for BakerUser
 */
export interface BakerUserFeaturePermissions {
  inventory: boolean;
  loans: boolean;
  lseg: boolean;
  dailyPricing: boolean;
  reports: boolean;
  portfolios: boolean;
  cashFlows: boolean;
  swapIQ: boolean;
  houstonTrust: boolean;
  empyreanImport: boolean;
  yieldBookMonitor: boolean;
}

/**
 * Maps profiles to their corresponding feature flag bundles
 * TODO: Store this in a database and fetch it from there. This will enable us to easily add/remove/edit profiles and their corresponding feature flags.
 */
export const bakerUserProfileToFeatureFlags: Record<BakerUserProfileOption, Partial<BakerUserFeaturePermissions>> = {
  management: {
    inventory: true,
    loans: true,
    lseg: true,
    dailyPricing: true,
    reports: true,
    portfolios: true,
    cashFlows: true,
    swapIQ: true,
    houstonTrust: true
  },
  sales: {
    inventory: true,
    portfolios: true,
    dailyPricing: true
  },
  fsgMuniCredit: {
    inventory: true
  },
  fsg: {
    inventory: true,
    lseg: true
  },
  bba: {
    lseg: true
  },
  irrm: {},
  clearing: {
    inventory: true
  },
  loans: {
    inventory: true,
    loans: true
  },
  desk: {
    inventory: true
  }
};

/**
 * Applies profile-based feature flags to a feature permissions object
 * @param profile - The profile to apply
 * @param baseFlags - Base flags to start with (defaults to all false)
 * @returns Feature permissions with profile flags applied
 */
export const applyBakerUserProfileFlags = (
  profile: string | null,
  baseFlags: BakerUserFeaturePermissions = {
    inventory: false,
    loans: false,
    lseg: false,
    dailyPricing: false,
    reports: false,
    portfolios: false,
    cashFlows: false,
    swapIQ: false,
    houstonTrust: false,
    empyreanImport: false,
    yieldBookMonitor: false
  }
): BakerUserFeaturePermissions => {
  const result = { ...baseFlags };

  if (profile && bakerUserProfileOptions.includes(profile as BakerUserProfileOption)) {
    const profileKey = profile as BakerUserProfileOption;
    const flags = bakerUserProfileToFeatureFlags[profileKey];
    Object.assign(result, flags);
  }

  return result;
};

/**
 * Applies manual feature permissions on top of profile-based flags.
 * This respects both manually added permissions (set to true) and manually removed permissions (set to false).
 * @param profile - The profile to apply base flags from
 * @param manualPermissions - Array of permission keys that should be enabled
 * @returns Feature permissions with profile flags and manual overrides applied
 */
export const applyManualPermissions = (
  profile: string | null,
  manualPermissions: string[]
): BakerUserFeaturePermissions => {
  // Start with profile-based flags
  const baseFlags = applyBakerUserProfileFlags(profile);

  // Create a set of manually selected permissions for quick lookup
  const manualPermissionsSet = new Set(manualPermissions);

  // Apply manual permissions: if in the array, set to true; if not, set to false
  const result: BakerUserFeaturePermissions = {
    inventory: manualPermissionsSet.has('inventory'),
    loans: manualPermissionsSet.has('loans'),
    lseg: manualPermissionsSet.has('lseg'),
    dailyPricing: manualPermissionsSet.has('dailyPricing'),
    reports: manualPermissionsSet.has('reports'),
    portfolios: manualPermissionsSet.has('portfolios'),
    cashFlows: manualPermissionsSet.has('cashFlows'),
    swapIQ: manualPermissionsSet.has('swapIQ'),
    houstonTrust: manualPermissionsSet.has('houstonTrust'),
    empyreanImport: manualPermissionsSet.has('empyreanImport'),
    yieldBookMonitor: manualPermissionsSet.has('yieldBookMonitor')
  };

  return result;
};
